import { useEffect, useRef, useState } from "react";
import useEnvioCodigoFisico from "hook/useEnvioCodigoFisico";
import { BuscarBoletoFisico } from "utils/BoletosFisicosQuery";

/* Captura de los codigos de los boletos FISICOS que se entregan en una venta (cualquier metodo
   de pago). Solo junta la lista: la asignacion a la compra la hace quien lo usa, con
   AsignarBoletosFisicosCompra, cuando la compra ya tiene id.
   - max: cuantos boletos tiene la venta (no deja agregar mas)
   - codigos / setCodigos: lista de codigos escaneados
   - secciones / setSecciones: codigo -> seccion del inventario (si el codigo esta en una sola) */
export default function CapturaBoletosFisicos({ max, codigos, setCodigos, secciones, setSecciones, idLector = "lector-fisico" }) {
    const [texto, setTexto] = useState("");
    const [aviso, setAviso] = useState(null);
    const [buscando, setBuscando] = useState(false);
    const [camara, setCamara] = useState(false);
    const scannerRef = useRef(null);
    const lleno = codigos.length >= max;

    const agregar = async (crudo) => {
        const codigo = String(crudo || "").trim();
        setTexto("");
        if (!codigo) return;
        if (codigos.includes(codigo)) return setAviso({ tipo: "warning", texto: `El código ${codigo} ya está en la lista` });
        if (codigos.length >= max) return setAviso({ tipo: "warning", texto: `Esta venta tiene ${max} boleto(s): ya están todos` });
        setCodigos((prev) => [...prev, codigo]);
        const codigoEvento = sessionStorage.getItem("eventoid");
        if (!codigoEvento) return;
        setBuscando(true);
        try {
            const r = await BuscarBoletoFisico(codigoEvento, codigo);
            const filas = r?.success && Array.isArray(r.data) ? r.data : [];
            const disponibles = filas.filter((f) => f.estado === "Disponible");
            if (filas.length === 0) setAviso({ tipo: "info", texto: `${codigo}: no está en el inventario, se registrará igual` });
            else if (disponibles.length === 0) setAviso({ tipo: "danger", texto: `${codigo}: ya fue vendido (${filas[0].estado}). Quítalo de la lista.` });
            else if (disponibles.length === 1) {
                setSecciones((prev) => ({ ...prev, [codigo]: disponibles[0].seccion }));
                setAviso({ tipo: "success", texto: `${codigo}: ${disponibles[0].seccion}${disponibles[0].fila_asiento ? " · " + disponibles[0].fila_asiento : ""}` });
            } else setAviso({ tipo: "warning", texto: `${codigo}: está en ${disponibles.length} secciones; podrás elegirla en el detalle de la orden` });
        } catch (_e) {
            setAviso(null);
        } finally {
            setBuscando(false);
        }
    };
    const { registrarTecleo, automatica, cambiarAutomatica, segundosManual } = useEnvioCodigoFisico({ valor: texto, activo: !lleno, enviar: agregar });

    const quitar = (codigo) => {
        setCodigos((prev) => prev.filter((c) => c !== codigo));
        setSecciones((prev) => { const n = { ...prev }; delete n[codigo]; return n; });
    };

    useEffect(() => {
        if (!camara) return undefined;
        // PERF: html5-qrcode (~320 KB) se descarga recien al abrir la cámara.
        // Este componente vive dentro de ModalPago/Modalefectivo, que la pagina
        // principal carga de entrada, y antes metia el lector en el JS inicial.
        let cancelado = false;
        import("html5-qrcode")
            .then(({ Html5Qrcode }) => {
                if (cancelado) return;
                const scanner = new Html5Qrcode(idLector);
                scannerRef.current = scanner;
                return scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 250 },
                    (leido) => { agregar(leido); setCamara(false); }, () => { /* sigue intentando */ });
            })
            .catch(() => { setAviso({ tipo: "danger", texto: "No se pudo abrir la cámara" }); setCamara(false); });
        return () => {
            cancelado = true;
            scannerRef.current?.stop().then(() => scannerRef.current?.clear()).catch(() => {});
            scannerRef.current = null;
        };
    }, [camara]);

    return (
        <div>
            <small className="text-muted d-block mb-1">
                <strong>{codigos.length} de {max}</strong> boleto(s) físico(s). Escanea o escribe el código y presiona Enter o <b>Buscar</b>.
                {buscando ? " Buscando…" : ""}
            </small>
            <div className="d-flex gap-2 align-items-center">
                <input className="form-control" type="text" value={texto} disabled={lleno} placeholder="código del boleto físico"
                    onChange={(e) => { registrarTecleo(e.target.value); setTexto(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregar(texto); } }} />
                <button type="button" className="btn btn-primary text-nowrap" disabled={lleno || !texto.trim()} onClick={() => agregar(texto)}>
                    <i className="fa fa-search" /> Buscar
                </button>
                <button type="button" className="btn btn-outline-primary" disabled={lleno} onClick={() => setCamara((v) => !v)} title="Escanear con la cámara">
                    <i className="fa fa-camera" />
                </button>
            </div>
            <div className="form-check mt-1">
                <input className="form-check-input" type="checkbox" id={`${idLector}-auto`} checked={automatica} onChange={(e) => cambiarAutomatica(e.target.checked)} />
                <label className="form-check-label" htmlFor={`${idLector}-auto`} style={{ fontSize: "0.85em" }}>
                    Buscar solo al dejar de escribir {segundosManual} s (el lector de código siempre busca al instante)
                </label>
            </div>
            <div className={camara ? "mt-2" : "d-none"}>
                <div id={idLector} style={{ width: "100%", maxWidth: 320 }} />
                {camara && <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={() => setCamara(false)}>Cancelar escaneo</button>}
            </div>
            {aviso && <div className={`alert alert-${aviso.tipo} py-1 px-2 mt-2 mb-0`} style={{ fontSize: "0.85em" }}>{aviso.texto}</div>}
            {codigos.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                    {codigos.map((c) => (
                        <span key={c} className="btn btn-secondary btn-sm rounded-pill px-3" title="Quitar" onClick={() => quitar(c)}>
                            {c}{secciones[c] ? ` · ${secciones[c]}` : ""} <b className="ms-1">×</b>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
